import { Breadcrumb, Button, Card, Col, Popconfirm, Row, Skeleton, Space, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { MatchingArgunment } from "../../common/UtilityInterfaces";
import { CommentsPanel } from "../comments/CommentsPanel";
import { MessageOutlined } from "@ant-design/icons";
import { TagPanel } from "../tag/TagPanel";
import {
    deleteVendor,
    getVendors,
    manageVendorIndustry,
    updateVendor,
} from "../../common/api/VendorClient";
import { DeleteVendorResponse, Vendor, VendorIndustry } from "../../types/VendorTypes";
import { ResourceType } from "../../types/ResourceType";
import { OrderFormMiniPanel } from "../orderform/components/OrderFormMiniPanel";
import { OrderFormStatus } from "../../types/OrderFormTypes";
import { Supplier } from "../../types/SupplierTypes";
import { isSuperAdmin } from "../../types/UserGroup";
import { getSupplierByName } from "../../common/api/SupplierClient";
import { handleError } from "../../common/ErrorHandling";
import { useUserState } from "../../common/UserContext";
import { getFullUrl } from "../../common/Url";
import { SvgIcon } from "../../common/components/SvgIcon";
import { CompanyImage } from "../../common/components/CompanyImage";
import { VendorUpdateModal } from "./components/VendorUpdateModal";
import { VendorDeleteModal } from "./components/VendorDeleteModal";

const { Title } = Typography;

interface VendorParams {
    vendorKey: string;
}

export const VendorDetails: React.FC<MatchingArgunment<VendorParams>> = () => {
    const { me } = useUserState();
    const navigate = useNavigate();
    const params = useParams();
    const [vendorKey] = useState(params.vendorKey);
    const [vendorData, setVendorData] = useState({} as Vendor);
    const [showMessages, setShowMessages] = useState(false);
    const [supplier, setSupplier] = useState<Supplier>();
    const [loading, setLoading] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);
    const [relatedData, setRelatedData] = useState({} as DeleteVendorResponse);
    const [showDelete, setShowDelete] = useState(false);

    const loadSupplier = async (name: string) => {
        const res = await getSupplierByName(name);
        setSupplier(res);
    };

    const loadVendor = async () => {
        setLoading(true);
        getVendors([vendorKey!])
            .then((vendors) => {
                setVendorData(vendors[0]);
            })
            .catch((err) => {})
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        loadVendor();
    }, [vendorKey]);

    useEffect(() => {
        if (vendorData.name) {
            loadSupplier(vendorData.name);
        }
    }, [vendorData]);

    const handleDelete = async () => {
        try {
            const res = await deleteVendor(vendorData);
            if (res.orderForms || res.negotiations) {
                setRelatedData(res);
                setShowDelete(true);
            } else {
                navigate("/vendor");
            }
        } catch (error) {
            handleError(error);
        }
    };

    const handleUpdate = async (updatedVendorData: Vendor, industry?: VendorIndustry) => {
        try {
            await updateVendor(updatedVendorData);
            if (industry?.vendorName && industry?.sectorId && industry.industryId) {
                await manageVendorIndustry(industry);
            }
            await loadVendor();
        } catch (error) {
            handleError(error);
        } finally {
            setShowUpdate(false);
        }
    };

    const messagesDrawer = showMessages ? (
        <>
            <CommentsPanel
                resourceId={vendorData.id}
                resourceType={ResourceType.Vendor}
                onClose={() => setShowMessages(false)}
            />
        </>
    ) : (
        <></>
    );

    const toolPanel = (
        <div style={{ paddingBottom: 10, paddingTop: 10 }}>
            <Row justify="start" align="middle">
                <Col span={24}>
                    {vendorData.id ? (
                        <TagPanel
                            resourceId={vendorData.id}
                            resourceType={ResourceType.Vendor}
                            readOnly={false}
                        />
                    ) : (
                        <></>
                    )}
                </Col>
            </Row>
        </div>
    );

    const toolbox = (
        <>
            <Space size={10}>
                {isSuperAdmin(me.roles || []) ? (
                    <Button type="primary" onClick={() => setShowUpdate(true)} size="small">
                        Edit
                    </Button>
                ) : null}
                {isSuperAdmin(me.roles || []) ? (
                    <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete()}>
                        <Button danger type="ghost" size="small">
                            Delete
                        </Button>
                    </Popconfirm>
                ) : null}
                <Button
                    size={"small"}
                    onClick={() => {
                        setShowMessages(true);
                    }}
                >
                    <MessageOutlined />
                </Button>
            </Space>
        </>
    );

    const breadcrumb = (
        <Breadcrumb key="orderBread" separator=">">
            <Breadcrumb.Item key={`vendor-bread`}>
                <Link to={"/vendor"} key="vendor">
                    Vendor
                </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item key={`vendor-bread`}>
                <Link to={`/vendor/${vendorData.vendorKey}`} key={`vendor-${vendorData.vendorKey}`}>
                    {vendorData.name}
                </Link>
            </Breadcrumb.Item>
        </Breadcrumb>
    );

    const contentPanel = (
        <Row gutter={[8, 8]}>
            <Col span={24}>{toolPanel}</Col>
            <Col span={16}>
                <Row gutter={[8, 8]}>
                    <Col span={24}>
                        <Card>
                            <Row align="middle">
                                <Col flex="70px">
                                    <CompanyImage
                                        logo={vendorData.hero || supplier?.logo}
                                        name={vendorData.name}
                                    />
                                </Col>
                                <Col flex="auto">
                                    <Title level={3}>
                                        <a href={vendorData.url || ""}>{vendorData.name}</a>
                                    </Title>
                                    <Space>
                                        {supplier?.website && (
                                            <a
                                                href={getFullUrl(supplier.website)}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ display: "inline-flex" }}
                                            >
                                                <SvgIcon name="global"></SvgIcon>
                                            </a>
                                        )}
                                        {supplier?.linkedIn && (
                                            <a
                                                href={supplier.linkedIn}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ display: "inline-flex" }}
                                            >
                                                <SvgIcon name="linkedin"></SvgIcon>
                                            </a>
                                        )}
                                        {supplier?.twitter && (
                                            <a
                                                href={supplier.twitter}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ display: "inline-flex" }}
                                            >
                                                <SvgIcon name="x"></SvgIcon>
                                            </a>
                                        )}
                                        {supplier?.id && (
                                            <Link
                                                to={`/supplier/${supplier.id}`}
                                                style={{ display: "inline-flex" }}
                                            >
                                                <SvgIcon name="briefs"></SvgIcon>
                                            </Link>
                                        )}
                                    </Space>
                                </Col>
                            </Row>
                            <Row>
                                <Col flex="70px"></Col>
                                <Col flex="auto">
                                    <span>{vendorData.description}</span>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Col>
            <Col span={8}>
                <Card></Card>
            </Col>
            <Col span={12}>
                {vendorData.id ? (
                    <OrderFormMiniPanel
                        title={"Active Orders"}
                        vendorKey={vendorData.vendorKey}
                        filterStatus={[
                            OrderFormStatus.EXPIRATION_NOT_SET,
                            OrderFormStatus.RENEWAL_SCHEDULED,
                            OrderFormStatus.OVERDUE,
                        ]}
                    />
                ) : (
                    <></>
                )}
            </Col>
            <Col span={12}>
                {vendorData.id ? (
                    <OrderFormMiniPanel
                        title={"Resolved orders"}
                        vendorKey={vendorData.vendorKey}
                        filterStatus={[OrderFormStatus.RENEWED, OrderFormStatus.WILL_NOT_RENEW]}
                    />
                ) : (
                    <></>
                )}
            </Col>
        </Row>
    );

    const content = (
        <div style={{ paddingTop: 10 }}>
            <VendorUpdateModal
                open={showUpdate}
                vendor={vendorData}
                onCancel={() => setShowUpdate(false)}
                onOk={handleUpdate}
            />
            <VendorDeleteModal
                open={showDelete}
                orderForms={relatedData.orderForms}
                negotiations={relatedData.negotiations}
                onCancel={() => setShowDelete(false)}
                onOk={() => setShowDelete(false)}
            />
            {messagesDrawer}
            <Skeleton loading={loading}>
                <Row key="orderContent">
                    <Col span={24}></Col>
                    <Col span={24}>
                        <Card bordered={false} bodyStyle={{ paddingTop: 10 }}>
                            <Row key="orderBread">
                                <Col span={20}>{breadcrumb}</Col>
                                <Col span={4}>
                                    <div style={{ textAlign: "right" }}>{toolbox}</div>
                                </Col>
                            </Row>
                            {contentPanel}
                        </Card>
                    </Col>
                </Row>
            </Skeleton>
        </div>
    );
    return content;
};
